import { ReactNode } from 'react';
import { useTranslation } from '@frontend/i18n';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { BannerNotification, Button, SpinningLoader, Text } from '@frontend/design-system';
import { Mode } from '../types';

type EmptyStateProps = {
  children?: ReactNode;
  setMode: (mode: Mode) => void;
};

export const EmptyState = ({ setMode }: EmptyStateProps) => {
  const { t } = useTranslation('bulk-messaging');

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: `calc(100% - ${theme.spacing(9)})`,
        padding: theme.spacing(0, 4),
        rowGap: theme.spacing(2),
      }}
    >
      <Text color='subdued' weight='bold'>
        {t('No Saved Lists')}
      </Text>
      <Text color='subdued' css={{ textAlign: 'center' }}>
        {t('Target a group of contacts from an uploaded csv.')}
      </Text>
      <Button
        css={{ display: 'flex', columnGap: theme.spacing(0.5), marginTop: theme.spacing(1) }}
        iconName='plus'
        onClick={() => setMode('csv')}
        trackingId={`${BulkEmailPrefixes.Audience}-contacts-empty-screen-upload-csv-btn`}
        variant='tertiary'
      >
        <Text color='primary'>{t('Upload List')}</Text>
      </Button>
    </div>
  );
};

type ErrorStateProps = {
  retry: () => void;
};

export const ErrorState = ({ retry }: ErrorStateProps) => {
  const { t } = useTranslation('bulk-messaging');

  return (
    <CenteredContainer>
      <BannerNotification status='error'>{t('Error loading lists.')}</BannerNotification>
      <Button onClick={() => retry()} variant='primary' css={{ marginTop: theme.spacing(2) }}>
        {t('Retry')}
      </Button>
    </CenteredContainer>
  );
};

type ListFooterProps = {
  hasMore: boolean;
  isFetchingNextPage: boolean;
};

export const ListFooter = ({ isFetchingNextPage, hasMore }: ListFooterProps) => {
  const { t } = useTranslation('bulk-messaging');

  if (isFetchingNextPage) {
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing(2),
        }}
      >
        <SpinningLoader />
      </div>
    );
  }

  if (!hasMore) {
    return (
      <Text css={{ margin: 0, padding: theme.spacing(3, 0), textAlign: 'center' }}>
        🎉 {t('You have reached the end')}
      </Text>
    );
  }

  return null;
};

export const LoadingState = () => (
  <CenteredContainer>
    <SpinningLoader />
  </CenteredContainer>
);

type CenteredContainerProps = {
  children: ReactNode;
};

const CenteredContainer = ({ children }: CenteredContainerProps) => (
  <div
    css={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: `calc(100% - ${theme.spacing(9)})`,
      flexDirection: 'column',
    }}
  >
    {children}
  </div>
);
